import styled, { css, keyframes } from 'styled-components';
import { FormMessage } from '../form-message/index.js';
import { getMargins, SEM_TYPOGRAPHY_FORMS_APPENDIX, SEM_TYPOGRAPHY_FORMS_INPUT, SEM_TYPOGRAPHY_FORMS_LABEL, SEM_TYPOGRAPHY_FORMS_LABEL_SMALL, getTypographyStyles, SYS_SPACE_FIXED_100, SYS_SPACE_FIXED_150, SYS_SPACE_FIXED_250, SYS_SIZE_FIXED_1400, SYS_SIZE_FIXED_600, SEM_BORDER_WIDTH_FORMS_ACTIVE, SEM_BORDER_WIDTH_FORMS_DEFAULT } from '@oneaudi/unified-web-common';
import { Icon } from '../icon/index.js';
import {} from './index.js';
const iconWidth = SYS_SIZE_FIXED_600;
// TODO Get motion tokens
const animationDuration = 250;
const animationTimingFunction = `cubic-bezier(0.75, 0.02, 0.5, 1)`;
const widthScaleFactorDecrease = 12 / 16;
const fontSizeBig = SEM_TYPOGRAPHY_FORMS_LABEL.fontSize; // 16px
const fontSizeSmall = SEM_TYPOGRAPHY_FORMS_LABEL_SMALL.fontSize; // 12px
const lineHeightBig = SEM_TYPOGRAPHY_FORMS_LABEL.lineHeight; // 20px
const inputContainerHeight = SYS_SIZE_FIXED_1400; // 56px
const inputHeight = `calc(${inputContainerHeight} - ${SYS_SPACE_FIXED_250})`; // 36px
const inputTopMargin = `calc((${inputHeight}) - ${lineHeightBig}) / 2`; // 8px
const translateY = `calc(${lineHeightBig} + ${inputTopMargin})`; // 28px
const floatKeyframes = (reverse = false, hasIcon = false) => {
    const stateSmall = css `
    width: ${100 * widthScaleFactorDecrease}%;
    font-size: ${fontSizeSmall};
    margin-left: 0;
  `;
    const stateBig = css `
    width: ${hasIcon
        ? `calc(100% - 2 * (${iconWidth} + ${SYS_SPACE_FIXED_150}))`
        : `calc(100% - (${iconWidth} + ${SYS_SPACE_FIXED_150}))`};
    font-size: ${fontSizeBig};
    transform: translateY(${translateY});
    margin-left: ${hasIcon ? `calc(${iconWidth} + ${SYS_SPACE_FIXED_150})` : 0};
  `;
    if (reverse === true) {
        return keyframes `
    0% {
      ${stateSmall}
    }
    100% {
      ${stateBig}
    }
  `;
    }
    return keyframes `
  0% {
    ${stateBig}
  }
  100% {
    ${stateSmall}
  }
  `;
};
export const StyledInputContainer = styled.div `
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  min-height: ${SYS_SIZE_FIXED_1400};
  flex: auto;
`;
export const StyledIconWrapper = styled.div `
  height: ${inputHeight};
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0;
  padding-bottom: 1px; // to account for the underline
  pointer-events: none;

  &.leadingWrapper {
    left: 0;
  }

  &.arrowWrapper {
    right: 0;
  }
`;
export const StyledLabel = styled.label `
  transform-origin: top left;
  // TODO: missing token
  transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  transition-duration: 250ms;
  transition-property: color;
  pointer-events: none;
  animation: ${(props) => props.value
    ? floatKeyframes(false, !!props.icon)
    : floatKeyframes(true, !!props.icon)}
    ${animationDuration}ms ${animationTimingFunction} both;

  ${getTypographyStyles(SEM_TYPOGRAPHY_FORMS_LABEL)}

  color: ${(props) => props.disabled
    ? props.theme.sem.color.forms.foreground.text.disabled
    : props.invalid
        ? props.theme.sem.color.forms.foreground.text.error
        : props.theme.sem.color.forms.foreground.text.default};
`;
export const StyledSelect = styled.select `
  padding: 0 calc(${iconWidth} + ${SYS_SPACE_FIXED_150}) 0
    ${(props) => props.icon ? `calc(${iconWidth} + ${SYS_SPACE_FIXED_150})` : '0'};
  background-color: transparent;
  border: none;
  width: 100%;
  appearance: none;
  // TODO Get motion tokens
  transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  transition-duration: 250ms;
  transition-property: color;
  ${getTypographyStyles(SEM_TYPOGRAPHY_FORMS_INPUT)}
  min-height: ${inputHeight};
  max-height: ${inputHeight};
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none;
  color: ${(props) => props.empty
    ? 'transparent'
    : props.disabled
        ? props.theme.sem.color.forms.foreground.text.disabled
        : props.theme.sem.color.forms.foreground.text.default};

  &:hover:enabled {
    color: ${(props) => !props.empty && props.theme.sem.color.forms.foreground.text.interactive};

    & + ${StyledLabel} {
      color: ${(props) => props.invalid
    ? props.theme.sem.color.forms.foreground.text.error
    : props.theme.sem.color.forms.foreground.text.interactive};
    }
  }

  &:focus,
  &:focus-visible:not(:read-only),
  &:active:not(:disabled) {
    color: ${(props) => props.disabled
    ? props.theme.sem.color.forms.foreground.text.disabled
    : props.theme.sem.color.forms.foreground.text.default};
    outline: auto;
    + ${StyledLabel} {
      animation: ${(props) => floatKeyframes(false, !!props.icon)}
        ${animationDuration}ms ${animationTimingFunction} both;
    }
  }

  ${(props) => props.readOnly &&
    css `
      pointer-events: none;
      user-select: text;
    `}
`;
export const StyledUpperGroup = styled.div `
  position: relative;
  display: flex;
  // TODO: tokens missing
  transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
  transition-duration: 250ms;
  transition-property: box-shadow, color;
  padding-bottom: 1px; // to account for the underline
  width: 100%;

  box-shadow: inset 0px -${SEM_BORDER_WIDTH_FORMS_DEFAULT} 0px 0px ${(props) => (props.disabled ? props.theme.sem.color.forms.surface.underline.stroke.disabled : props.invalid ? props.theme.sem.color.forms.surface.underline.stroke.error : props.theme.sem.color.forms.surface.underline.stroke.default)};

  & .arrow {
    // TODO Get motion tokens
    transition-timing-function: cubic-bezier(0.75, 0.02, 0.5, 1);
    transition-duration: 250ms;
    transition-property: transform;
  }

  ${(props) => !props.disabled &&
    css `
      &:has(${StyledSelect}:focus) .arrow,
      &:has(${StyledSelect}:active) .arrow {
        transform: rotateZ(180deg);
      }
    `}

  ${(props) => !props.disabled &&
    css `
      &:has(${StyledSelect}:active, ${StyledSelect}:focus) {
        box-shadow: inset 0px -${SEM_BORDER_WIDTH_FORMS_ACTIVE} 0px 0px ${props.invalid ? props.theme.sem.color.forms.surface.underline.stroke.error : props.theme.sem.color.forms.surface.underline.stroke.interactive};
      }
    `}
`;
export const StyledSelectField = styled.div `
  margin: ${(props) => getMargins(props.margin)};
  display: flex;
  flex-direction: column;
  width: 100%;
`;
export const StyledFormMessage = styled(FormMessage) `
  padding-top: ${SYS_SPACE_FIXED_150};
`;
export const StyledAppendix = styled.span `
  color: ${(props) => props.theme.sem.color.forms.foreground.text.caption};
  ${getTypographyStyles(SEM_TYPOGRAPHY_FORMS_APPENDIX)}
  margin-left: ${SYS_SPACE_FIXED_100};
`;
export const StyledIcon = styled(Icon) `
  // TODO Get Motion Tokens
  transition: all 0.2s ease;
  pointer-events: none;
  color: ${(props) => props.disabled
    ? props.theme.sem.color.forms.foreground.icons.disabled
    : props.invalid
        ? props.theme.sem.color.forms.foreground.icons.error
        : props.theme.sem.color.forms.foreground.icons.default};

  & + ${StyledSelect}:hover:enabled {
    color: ${(props) => !props.invalid &&
    props.theme.sem.color.forms.foreground.icons.interactive};
  }
`;
//# sourceMappingURL=index.styles.js.map